import React from 'react';
import type { CanvasSectionBlock } from '../sectionRegistry';

type ScaleInteraction = {
  pointerId: number;
  blockId: string;
  axis: 'x' | 'y' | 'both';
  startScaleX: number;
  startScaleY: number;
  startClientX: number;
  startClientY: number;
};

type UseProductCardScaleControlsOptions = {
  scaleMode: boolean;
  onBlockScaleChange?: (blockId: string, scale: { x: number; y: number }) => void;
  selectBlock: (blockId: string) => void;
};

export function useProductCardScaleControls({
  scaleMode,
  onBlockScaleChange,
  selectBlock,
}: UseProductCardScaleControlsOptions) {
  const scaleInteractionRef = React.useRef<ScaleInteraction | null>(null);

  const beginScale = React.useCallback(
    (event: React.PointerEvent<HTMLButtonElement>, block: CanvasSectionBlock, axis: 'x' | 'y' | 'both') => {
      if (!scaleMode || block.type !== 'product-card' || !onBlockScaleChange) return;
      event.preventDefault();
      event.stopPropagation();
      selectBlock(block.id);
      const content =
        block.content && typeof block.content === 'object'
          ? (block.content as Record<string, unknown>)
          : {};
      const fallbackScale = Math.max(0.5, Math.min(1.25, Number(content.contentScale) || 1));
      const startScaleX = Math.max(0.5, Math.min(1.25, Number(content.contentScaleX) || fallbackScale));
      const startScaleY = Math.max(0.5, Math.min(1.25, Number(content.contentScaleY) || fallbackScale));
      scaleInteractionRef.current = {
        pointerId: event.pointerId,
        blockId: block.id,
        axis,
        startScaleX,
        startScaleY,
        startClientX: event.clientX,
        startClientY: event.clientY,
      };
      event.currentTarget.setPointerCapture?.(event.pointerId);
    },
    [onBlockScaleChange, scaleMode, selectBlock]
  );

  const moveScale = React.useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      const interaction = scaleInteractionRef.current;
      if (!interaction || interaction.pointerId !== event.pointerId || !onBlockScaleChange) return;
      event.preventDefault();
      event.stopPropagation();
      const deltaX = (event.clientX - interaction.startClientX) / 240;
      const deltaY = (event.clientY - interaction.startClientY) / 240;
      const nextScaleX =
        interaction.axis === 'y'
          ? interaction.startScaleX
          : interaction.axis === 'both'
            ? (() => {
                const dominantDelta = Math.abs(deltaX) >= Math.abs(deltaY) ? deltaX : deltaY;
                const minFactor = Math.max(
                  0.5 / Math.max(0.01, interaction.startScaleX),
                  0.5 / Math.max(0.01, interaction.startScaleY)
                );
                const maxFactor = Math.min(
                  1.25 / Math.max(0.01, interaction.startScaleX),
                  1.25 / Math.max(0.01, interaction.startScaleY)
                );
                const factor = Math.max(minFactor, Math.min(maxFactor, 1 + dominantDelta));
                return Number((interaction.startScaleX * factor).toFixed(2));
              })()
            : Math.max(0.5, Math.min(1.25, Number((interaction.startScaleX + deltaX).toFixed(2))));
      const nextScaleY =
        interaction.axis === 'x'
          ? interaction.startScaleY
          : interaction.axis === 'both'
            ? (() => {
                const dominantDelta = Math.abs(deltaX) >= Math.abs(deltaY) ? deltaX : deltaY;
                const minFactor = Math.max(
                  0.5 / Math.max(0.01, interaction.startScaleX),
                  0.5 / Math.max(0.01, interaction.startScaleY)
                );
                const maxFactor = Math.min(
                  1.25 / Math.max(0.01, interaction.startScaleX),
                  1.25 / Math.max(0.01, interaction.startScaleY)
                );
                const factor = Math.max(minFactor, Math.min(maxFactor, 1 + dominantDelta));
                return Number((interaction.startScaleY * factor).toFixed(2));
              })()
            : Math.max(0.5, Math.min(1.25, Number((interaction.startScaleY + deltaY).toFixed(2))));
      onBlockScaleChange(interaction.blockId, {
        x: nextScaleX,
        y: nextScaleY,
      });
    },
    [onBlockScaleChange]
  );

  const endScale = React.useCallback((event: React.PointerEvent<HTMLButtonElement>) => {
    const interaction = scaleInteractionRef.current;
    if (!interaction || interaction.pointerId !== event.pointerId) return;
    event.preventDefault();
    event.stopPropagation();
    scaleInteractionRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }, []);

  return { beginScale, moveScale, endScale };
}
